import { Input, Select, Spin } from 'antd';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
	fetchCapExCategoriesForBuyer,
	fetchCapExCategoriesForSupplier,
	fetchRMCategoriesForBuyer,
	fetchRMCategoriesForSupplier,
} from '../../thunks/invoices_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { nullSafeGet } from '../../utils/DataAccessUtils';

const CATEGORY_CONFIG = {
	capex: {
		categoryIdField: 'capExCategoryId',
		categoryField: 'capExCategory',
		selectPlaceholder: 'Select a CapEx category',
		inputPlaceholder: 'Enter a CapEx category',
	},
	rm: {
		categoryIdField: 'rmCategoryId',
		categoryField: 'rmCategory',
		selectPlaceholder: 'Select an R&M category',
		inputPlaceholder: 'Enter an R&M category',
	},
};

interface ExpenseCategoryInputProps {
	categoryType: 'capex' | 'rm';
	fetchCategories?: any;
	value?: any;
	onChange?: any;
}

const ExpenseCategoryInput: FC<any> = ({
	categoryType,
	fetchCategories,
	value,
	onChange,
}): React.ReactElement => {
	const config = CATEGORY_CONFIG[categoryType];
	const categoryId = nullSafeGet(config.categoryIdField, value);
	const category = nullSafeGet(config.categoryField, value);

	const [categoriesLoading, setCategoriesLoading] = useState(false);
	const [categories, setCategories] = useState([]);

	const isCategoriesAvailable = useMemo(() => categories.length > 0, [categories]);

	useEffect(() => {
		setCategoriesLoading(true);
		fetchCategories({ limit: 50 })
			.then((res) => setCategories(res.map((cat) => ({ value: cat.id, label: cat.name }))))
			.finally(() => setCategoriesLoading(false));
	}, []);

	const onValueChange = useCallback(
		(val) => {
			if (isCategoriesAvailable) {
				const selectedCategory = categories.find((_) => _.value === val);
				onChange({
					[config.categoryIdField]: nullSafeGet('value', selectedCategory),
					[config.categoryField]: nullSafeGet('label', selectedCategory) || '',
				});
			} else {
				onChange({
					[config.categoryIdField]: undefined,
					[config.categoryField]: val,
				});
			}
		},
		[categories, isCategoriesAvailable, onChange, config]
	);

	const modifiedValue = useMemo(() => {
		return categoryId ? categoryId : category;
	}, [categoryId, category]);

	return categoriesLoading ? (
		<Spin />
	) : isCategoriesAvailable ? (
		<Select
			value={modifiedValue}
			onChange={onValueChange}
			placeholder={config.selectPlaceholder}
			options={categories}
			style={{ maxWidth: 640 }}
		/>
	) : (
		<Input
			value={modifiedValue}
			onChange={(e) => onValueChange(e.target.value)}
			placeholder={config.inputPlaceholder}
			style={{ maxWidth: 640 }}
		/>
	);
};

const FETCH_MAP = {
	capex: {
		[ROLE_TYPES.BUYER]: fetchCapExCategoriesForBuyer,
		[ROLE_TYPES.SUPPLIER]: fetchCapExCategoriesForSupplier,
	},
	rm: {
		[ROLE_TYPES.BUYER]: fetchRMCategoriesForBuyer,
		[ROLE_TYPES.SUPPLIER]: fetchRMCategoriesForSupplier,
	},
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchCategories: (params) =>
		dispatch(FETCH_MAP[ownProps.categoryType][ownProps.userType](params)),
});

const ComponentWithoutUserType = withRouter(
	connect(null, mapDispatchToProps)(ExpenseCategoryInput)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
